import React from "react";
import {
  Outlet,
  useLoaderData,
  Navigate,
  useLocation,
  Await,
} from "react-router-dom";
import { roleRoutes } from "@src/routes/routes";
import { useAuthContext } from "@src/context/authContext";
import DynamicContentProvider from "@src/context/dcContext";
import { PageErrorContextProvider } from "@src/context/pageErrorContext";
import { Loader, Button } from "@src/components/atoms";
import DefaultAppLayout from "@src/components/layout/DefaultAppLayout";
import ResetableErrorBoundary from "@src/components/utility/ResettableErrorBoundary";
import StorageUtils from "@src/utils/storageUtils";
import {
  LOGIN_PENDING_REDIRECT_COOKIE,
  ROOT_LOADING_MESSAGES,
  ROOT_LOADING_MESSAGE_INTERVAL_MS,
  LOGGING_OUT,
} from "@src/constants/constants";
import RootAppLayout from "@src/components/layout/RootAppLayout";
import { RoleSwitcher } from "@src/components/layout";
import { useChangeLogger } from "@src/components/utility/useChangeLogger";
import LoaderFullPage from "../components/atoms/LoaderFullPage";

const AuthenticatedApp = () => {
  const { pathname, search } = useLocation();
  const {
    isAuthenticated,
    activeRole,
    activeRoleRoutePrefix,
    hasRoleSwitcher,
  } = useAuthContext();
  const { dynamicContent } = useLoaderData();

  //TEMP: Logging
  useChangeLogger("AuthenticatedApp", {
    pathname,
    dynamicContent,
    isAuthenticated,
    activeRole,
    activeRoleRoutePrefix,
  });

  console.log("authenticated app", activeRole);

  const pathParts = pathname.split("/");
  if (!isAuthenticated) {
    if (pathParts.length > 2) {
      // if user is not authenticated, redirect to login page
      // and store the current path to redirect after successful login
      if (StorageUtils.getItem(LOGGING_OUT) !== null) {
        StorageUtils.removeItem(LOGGING_OUT);
      } else {
        StorageUtils.setItem(LOGIN_PENDING_REDIRECT_COOKIE, pathname);
      }
    }
    // protect private routes — forward query params so utm_source etc. survive the redirect
    const loginTarget = search ? `/login${search}` : "/";
    return <Navigate to={loginTarget} replace />;
  }
  if (pathname.startsWith("/dashboard/") && !activeRole) {
    console.debug("activeRole missing");
    // redirect to role selector if no role is selected
    return <Navigate to="/dashboard#select-role" replace />;
  }
  let redirectPath = StorageUtils.getItem(LOGIN_PENDING_REDIRECT_COOKIE);
  if (
    redirectPath &&
    activeRole &&
    redirectPath.includes(activeRoleRoutePrefix)
  ) {
    StorageUtils.removeItem(LOGIN_PENDING_REDIRECT_COOKIE);
    if (pathname != redirectPath) {
      console.debug(`redirect (redirectPath) ${pathname} -> ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
    }
  } else if (pathname === "/dashboard" && activeRole && activeRoleRoutePrefix) {
    // plain "/dashboard" should redirect to role-specific overview page
    const pathRoleOverviewPage = `/dashboard/${activeRoleRoutePrefix}/overview`;
    console.debug(`redirect ${pathname} -> ${pathRoleOverviewPage}`);
    return <Navigate to={pathRoleOverviewPage} replace />;
  }

  const currentPage = pathname.split("/").at(-1) || "overview";
  // prepare menu/nav items based on chosen role
  const roleMenu = roleRoutes[activeRole]?.children;

  const DashboardSkeleton = () => (
    /* TODO: evaluate using <Loader /> or <Skeleton/> from 'react-loading-skeleton' or similar? */
    <LoaderFullPage
      messages={ROOT_LOADING_MESSAGES}
      intervalMs={ROOT_LOADING_MESSAGE_INTERVAL_MS}
    />
  );

  const containerWidth = ["ADMINISTRATOR", "FUEL_MANAGER"].includes(activeRole)
    ? "1312px"
    : "1120px";

  return (
    <React.Suspense fallback={<DashboardSkeleton />}>
      <Await resolve={dynamicContent}>
        {(dynamicContent) => (
          <DynamicContentProvider dynamicContent={dynamicContent}>
            {hasRoleSwitcher() && !activeRole ? (
              // logged user must select a role to enter to corresponding signol app's module
              //<ModuleSelector />
              <RootAppLayout>
                <RoleSwitcher />
              </RootAppLayout>
            ) : (
              // display corresponding inner module container (protected route based on role)
              <PageErrorContextProvider>
                <DefaultAppLayout
                  navItems={roleMenu}
                  containerWidth={containerWidth}
                  currentPage={currentPage}
                >
                  <ResetableErrorBoundary>
                    <Outlet />
                  </ResetableErrorBoundary>
                </DefaultAppLayout>
              </PageErrorContextProvider>
            )}
          </DynamicContentProvider>
        )}
      </Await>
    </React.Suspense>
  );
};

export default AuthenticatedApp;
